import React, { useCallback, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { verticalFrame } from '../lib/dataset-points3d';
import { buildScatterBuffers, scatterLabel, scatterOrigin } from '../lib/raw-scatter3d';

const PICK_RADIUS = 0.6; // scene units (m) around a point that still count as a hover hit

function ScatterPoints({ ext, origin, refElev, groundY, flatBase, onHover }) {
  const geometry = useMemo(() => {
    const b = buildScatterBuffers(ext, origin, Number.isFinite(refElev) ? verticalFrame({ refElev, groundY }) : null, { flatBase });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(b.positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(b.colors, 3));
    return g;
  }, [ext, origin, refElev, groundY, flatBase]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const raycaster = useThree((s) => s.raycaster);
  useEffect(() => {
    const params = raycaster.params.Points;
    const before = params.threshold;
    params.threshold = PICK_RADIUS;
    return () => { params.threshold = before; };
  }, [raycaster]);

  const move = useCallback((e) => {
    if (!onHover || e.index == null) return;
    e.stopPropagation();
    onHover({ text: scatterLabel(ext, e.index), x: e.nativeEvent?.clientX ?? 0, y: e.nativeEvent?.clientY ?? 0 });
  }, [ext, onHover]);
  const out = useCallback(() => { if (onHover) onHover(null); }, [onHover]);

  return (
    <points geometry={geometry} frustumCulled={false} onPointerMove={move} onPointerOut={out}>
      <pointsMaterial vertexColors size={3} sizeAttenuation={false} />
    </points>
  );
}

/**
 * Apparent-resistivity scatter of raw surveys: one THREE.Points per ticked survey (`scatters`: id -> extract from
 * hooks/useRawScatter), one point per reading at its pseudo-position (plan-view centroid of the four electrodes for a
 * 3D grid, along-line midpoint for a 2D line) and its pseudo-depth below the ground, coloured by log10 rhoa
 * (viridis). Placed in the shared local frame `origin`, rendered inside the terrain group like DatasetPointClouds;
 * each reading hangs from its `surface_z` (flat scene: relative to `flatBase`, the lowest model surface).
 * `onHover({ text, x, y } | null)` receives the tooltip for the point under the cursor.
 */
export default function RawScatter3D({ datasets, scatters, origin, refElev = null, groundY = null, flatBase = null, onHover = null }) {
  const ready = datasets.map((d) => ({ id: d.id, ext: scatters?.[d.id] })).filter((c) => c.ext && c.ext.count > 0);
  const firstExt = ready[0]?.ext;
  const frame = useMemo(() => origin || scatterOrigin(firstExt), [origin, firstExt]);
  if (!frame) return null;
  return <>{ready.map((c) => <ScatterPoints key={c.id} ext={c.ext} origin={frame} refElev={refElev} groundY={groundY} flatBase={flatBase} onHover={onHover} />)}</>;
}
